/**
 * frontend/src/pages/valuation/_MethodologyEarnings.tsx
 *
 * VAL-PATH-SIGNER P7b — the earnings block on Methodology and rates. The
 * applied figure is shown, never typed (it comes from normalization's Apply).
 * The signer may override it and may choose the basis (ADD 10-22 06:40), each
 * with a required reason; a role without valuation:override sees the figure only.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { money } from '@/utils/format'
import { Field } from './_AdjustLegs'
import { basisLabel } from './_shared'
import type { BasisChoice, useMethodologyEdits } from './_useMethodologyEdits'

/** OWNER_APPROVALS.log 2026-10-22 14:45 (P7B), verbatim. */
export const EARNINGS_HEADING = 'Earnings'
export const APPLIED_EARNINGS_LABEL = 'Earnings used in the valuation'
export const EARNINGS_OVERRIDE_LABEL = 'Override the earnings ($)'
export const EARNINGS_OVERRIDE_REASON_LABEL = 'Reason for the earnings override'
export const EARNINGS_BASIS_LABEL = 'Earnings basis'
export const BASIS_REASON_LABEL = 'Reason for the earnings basis'
/** The threshold is the signed crossover from config (earnings_basis_rule), never a literal. */
export const basisAutomatic = (threshold: number) =>
  `Automatic (SDE below ${money(threshold)}, adjusted EBITDA at or above)`

type Earnings = ReturnType<typeof useMethodologyEdits>['earnings']

export function MethodologyEarnings({
  earnings: e, editable, threshold,
}: {
  earnings: Earnings
  /** The signer, once the draft is in (VAL-DRAFT-OVERWRITE-1). */
  editable: boolean
  /** The signed crossover (config earnings_basis_rule); null until the config loads. */
  threshold: number | null
}) {
  return (
    <section aria-label={EARNINGS_HEADING}>
      <Card>
        <CardHeader><CardTitle>{EARNINGS_HEADING}</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <dl className="text-sm">
            <dt className="text-gray-600">{APPLIED_EARNINGS_LABEL}</dt>
            <dd className="font-medium text-gray-900">
              {e.applied !== null ? money(e.applied) : <Skeleton className="h-4 w-24" />}
            </dd>
          </dl>
          {editable && (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field id="earnings_override" label={EARNINGS_OVERRIDE_LABEL}
                  value={e.earningsOverride} onChange={e.setEarningsOverride} />
                <Field id="earnings_override_reason" type="text" label={EARNINGS_OVERRIDE_REASON_LABEL}
                  value={e.earningsOverrideReason} onChange={e.setEarningsOverrideReason} />
              </div>
              {/* OWNER_APPROVALS.log 10-21 05:05 — 'Your override replaces the calculated $X'. */}
              {e.overrideApplies && e.calculated !== null && (
                <p className="text-xs text-gray-600">Your override replaces the calculated {money(e.calculated)}</p>
              )}
              {e.overrideMessage && <p className="text-xs text-red-700">{e.overrideMessage}</p>}
              {threshold !== null && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="earnings_basis" className="text-xs">{EARNINGS_BASIS_LABEL}</Label>
                    <select
                      id="earnings_basis"
                      className="h-9 w-full rounded-md border border-gray-300 bg-white px-2 text-sm"
                      value={e.basisChoice}
                      onChange={ev => e.setBasisChoice(ev.target.value as BasisChoice)}
                    >
                      <option value="">{basisAutomatic(threshold)}</option>
                      <option value="sde">{basisLabel('sde')}</option>
                      <option value="adjusted_ebitda">{basisLabel('adjusted_ebitda')}</option>
                    </select>
                  </div>
                  {e.basisChoice !== '' && (
                    <Field id="earnings_basis_reason" type="text" label={BASIS_REASON_LABEL}
                      value={e.basisReason} onChange={e.setBasisReason} />
                  )}
                </div>
              )}
              {e.basisMessage && <p className="text-xs text-red-700">{e.basisMessage}</p>}
            </>
          )}
        </CardContent>
      </Card>
    </section>
  )
}
